import { create } from 'zustand';
import { SubscribeDevClient, SubscribeDevClientConfig } from '@subscribe.dev/client';

interface UserObject {
    userId: string;
    email: string;
    avatarUrl?: string;
}

interface AuthState {
    isReady: boolean;
    isSignedIn: boolean;
    token: string | null;
    user: UserObject | null;
    client: SubscribeDevClient | null;
    hasSignedOut: boolean;
    subscriptionModalUrl: string | null;
    deferredAccessToken?: string;

    // Store original initialization parameters for signOut reinitializion
    originalInitParams: {
        projectToken?: string;
        baseUrl?: string;
        authorizationUrl?: string;
        accessToken?: string;
        autoSignIn?: boolean;
        userToken?: string;
        getUserToken?: () => Promise<string>;
    } | null;

    // Derived state
    canFetchUsage: boolean;
    canUseStorage: boolean;
}

interface AuthActions {
    initializeAuth: (props: {
        projectToken?: string;
        baseUrl?: string;
        authorizationUrl?: string;
        accessToken?: string;
        autoSignIn?: boolean;
        userToken?: string;
        getUserToken?: () => Promise<string>;
    }) => void;
    signIn: () => void;
    signOut: () => void;
    setSubscriptionModalUrl: (url: string | null) => void;
    handleAuthCallback: (accessToken: string) => void;

    // Internal actions
    _setPersistedToken: (token: string | null) => void;
    _setHasSignedOut: (hasSignedOut: boolean) => void;
}

export type SubscribeDevStore = AuthState & AuthActions;

// Function to decode JWT token and extract user information
function decodeJWT(token: string): UserObject | null {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;

        const decoded = JSON.parse(atob(payload));

        return {
            userId: decoded.sub || '',
            email: decoded.email || '',
            avatarUrl: decoded.avatarUrl
        };
    } catch (error) {
        console.warn('Failed to decode JWT token:', error);
        return null;
    }
}

// Function to check if a JWT token is expired or about to expire
function isTokenExpired(token: string): boolean {
    try {
        const payload = token.split('.')[1];
        if (!payload) return true;

        const decoded = JSON.parse(atob(payload));
        const exp = decoded.exp;

        if (!exp) return true;

        // Consider token expired if it expires within the next 30 seconds (buffer for clock skew)
        const now = Math.floor(Date.now() / 1000);
        const isExpired = exp <= now + 30;

        if (isExpired) {
            console.log('[SubscribeDevStore] Token is expired or expiring soon', {
                exp,
                now,
                expiresIn: exp - now
            });
        }

        return isExpired;
    } catch (error) {
        console.warn('Failed to check JWT token expiration:', error);
        return true; // Treat invalid tokens as expired
    }
}

// Utility function to extract and remove accessToken from URL
function extractAndRemoveAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('accessToken');
    
    if (accessToken) {
        // Remove the accessToken from URL to avoid exposing it
        urlParams.delete('accessToken');
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);
        return accessToken;
    }
    
    return null;
}

export const useSubscribeDevStore = create<SubscribeDevStore>((set, get) => ({
    // Initial state
    isReady: false,
    isSignedIn: false,
    token: null,
    user: null,
    client: null,
    hasSignedOut: false,
    subscriptionModalUrl: null,
    deferredAccessToken: undefined,
    originalInitParams: null,
    canFetchUsage: false,
    canUseStorage: false,

    initializeAuth: ({ projectToken, baseUrl, authorizationUrl, accessToken, autoSignIn, userToken, getUserToken }) => {
        console.log('[SubscribeDevStore] Initializing auth...');

        // Store original initialization parameters for later use in signOut
        set(state => ({
            ...state,
            originalInitParams: { projectToken, baseUrl, authorizationUrl, accessToken, autoSignIn, userToken, getUserToken }
        }));
        
        // Extract and persist accessToken from URL query parameter
        let persistedToken: string | null = null;
        if (typeof window !== 'undefined') {
            const urlToken = extractAndRemoveAccessToken();
            if (urlToken) {
                // Store in localStorage for persistence across browser sessions
                localStorage.setItem('subscribe-dev-access-token', urlToken);
                persistedToken = urlToken;
            } else {
                // Check if we have a previously stored token
                persistedToken = localStorage.getItem('subscribe-dev-access-token');
            }
        }

        const state = get();
        
        // Token priority: prop accessToken > persisted URL accessToken > userToken + projectToken (headless auth) > no token (demo mode)
        // But if user has signed out, clear the token
        let token: string | null = null;
        let isSignedIn = false;
        
        console.log('[SubscribeDevStore] Token resolution:', {
            hasSignedOut: state.hasSignedOut,
            accessTokenProp: accessToken ? accessToken.substring(0, 10) + '...' : 'none',
            persistedToken: persistedToken ? persistedToken.substring(0, 10) + '...' : 'none',
            userToken: userToken ? userToken.substring(0, 10) + '...' : 'none',
            projectToken: projectToken ? projectToken.substring(0, 10) + '...' : 'none'
        });
        
        if (!state.hasSignedOut) {
            if (accessToken) {
                // Check if token is expired before using it
                if (isTokenExpired(accessToken)) {
                    console.log('[SubscribeDevStore] accessToken prop is expired, signing out');
                    // Clear localStorage and trigger signOut
                    if (typeof window !== 'undefined') {
                        localStorage.removeItem('subscribe-dev-access-token');
                    }
                    // Will fall through to no token state
                } else if (autoSignIn) {
                    // Opt-in to auto sign-in (old behavior)
                    token = accessToken;
                    isSignedIn = true;
                    console.log('[SubscribeDevStore] Using accessToken prop with autoSignIn=true');
                } else {
                    // DEFAULT: Defer authentication (store token but don't sign in yet)
                    // This is the default because accessToken is only used in local dev
                    // and developers usually want to test both signed-out and signed-in states
                    set(prevState => ({
                        ...prevState,
                        deferredAccessToken: accessToken
                    }));
                    console.log('[SubscribeDevStore] Deferred accessToken prop (will sign in on explicit call). Set autoSignIn=true to auto sign-in.');
                    // Don't set token or isSignedIn - fall through
                }
            } else if (persistedToken) {
                // Check if persisted token is expired before using it
                if (isTokenExpired(persistedToken)) {
                    console.log('[SubscribeDevStore] Persisted token is expired, signing out');
                    // Clear localStorage and trigger signOut
                    if (typeof window !== 'undefined') {
                        localStorage.removeItem('subscribe-dev-access-token');
                    }
                    // Will fall through to no token state
                } else {
                    // Persisted URL access token takes second priority
                    token = persistedToken;
                    isSignedIn = true;
                    console.log('[SubscribeDevStore] Using persisted token');
                }
            } else if (userToken && projectToken) {
                // Headless auth: userToken + projectToken combination
                token = userToken;
                isSignedIn = true;
                console.log('[SubscribeDevStore] Using headless auth (userToken + projectToken)');
            } else if (getUserToken && projectToken) {
                // Async getUserToken function provided - we'll handle this differently
                console.log('[SubscribeDevStore] getUserToken function provided - will create client with async token retrieval');
                // Don't set token yet - we'll create a special client that uses getUserToken
                isSignedIn = true;
            } else {
                console.log('[SubscribeDevStore] No tokens available - demo mode');
            }
        } else {
            console.log('[SubscribeDevStore] User has signed out - will create client with project token only');
        }

        // Extract user information from JWT token
        // For headless auth, extract user info from userToken; otherwise use token
        let user: UserObject | null = null;
        if (userToken && projectToken && isSignedIn) {
            // In headless auth mode, decode user info from userToken
            user = decodeJWT(userToken);
        } else if (token) {
            // In normal auth mode, decode user info from access token
            user = decodeJWT(token);
        }

        // Create client configuration
        const config: SubscribeDevClientConfig = {
            debug: true
        };

        if (baseUrl) {
            config.baseUrl = baseUrl;
        }
        if (authorizationUrl) {
            config.authorizationUrl = authorizationUrl;
        }

        console.log('[SubscribeDevStore] Creating client with config:', {
            hasAccessToken: !!token,
            hasProjectToken: !!projectToken,
            hasAccessTokenProp: !!accessToken,
            hasGetUserToken: !!getUserToken,
            isSignedIn,
            tokenPrefix: token ? token.substring(0, 10) + '...' : 'none',
            accessTokenPropPrefix: accessToken ? accessToken.substring(0, 10) + '...' : 'none',
            finalTokenUsed: (accessToken || token) ? (accessToken || token)?.substring(0, 10) + '...' : 'none'
        });

        let client: SubscribeDevClient;
        let canFetchUsage = false;
        let canUseStorage = false;

        // State 3: With access token - universal auth (access token serves as both access token and user key)
        if (isSignedIn && token && (accessToken || persistedToken)) {
            const authConfig = {
                ...config,
                accessToken: token,
                userKey: token, // Access token also serves as userKey for user-specific operations
                // Ensure no conflicting auth tokens
                apiKey: undefined
            };
            client = new SubscribeDevClient(authConfig);
            canFetchUsage = true; // Can fetch usage with userKey
            canUseStorage = true; // Can use remote storage with access token
            console.log('[SubscribeDevStore] Created client with universal access token auth');
        }
        // State 3b: Headless auth - userToken + projectToken
        else if (isSignedIn && token && userToken && projectToken) {
            const authConfig = {
                ...config,
                apiKey: projectToken,
                userKey: userToken, // userToken serves as userKey for user-specific operations
                // No access token for headless auth
                accessToken: undefined
            };
            client = new SubscribeDevClient(authConfig);
            canFetchUsage = true; // Can fetch usage with userKey
            canUseStorage = false; // Cannot use remote storage without access token
            console.log('[SubscribeDevStore] Created client with headless auth (userToken + projectToken)');
        }
        // State 3c: Async getUserToken function with projectToken
        else if (isSignedIn && getUserToken && projectToken) {
            // Create a custom client that uses getUserToken dynamically
            const authConfig = {
                ...config,
                apiKey: projectToken,
                getUserToken, // Pass the async function to the client
                // No static tokens
                accessToken: undefined,
                userKey: undefined
            };
            client = new SubscribeDevClient(authConfig);
            canFetchUsage = true; // Can fetch usage with async getUserToken
            canUseStorage = false; // Cannot use remote storage without access token
            console.log('[SubscribeDevStore] Created client with async getUserToken function');
            
            // Immediately call getUserToken to populate the token state
            // This ensures the token is available for subscription modal and other operations
            (async () => {
                try {
                    const userToken = await getUserToken();
                    if (userToken) {
                        console.log('[SubscribeDevStore] Retrieved user token from getUserToken function');
                        const userFromToken = decodeJWT(userToken);
                        
                        // Recreate client with the user token as userKey
                        const newAuthConfig = {
                            ...config,
                            apiKey: projectToken,
                            userKey: userToken, // Now we have the userKey for subscription status calls
                            accessToken: undefined
                        };
                        const newClient = new SubscribeDevClient(newAuthConfig);
                        console.log('[SubscribeDevStore] Recreated client with userKey from getUserToken');
                        
                        set(currentState => ({
                            ...currentState,
                            token: userToken,
                            user: userFromToken,
                            client: newClient, // Replace the client with the new one that has userKey
                            canFetchUsage: true, // Can fetch usage with userKey
                            canUseStorage: false // Cannot use remote storage without access token
                        }));
                    }
                } catch (error) {
                    console.warn('[SubscribeDevStore] Failed to retrieve user token from getUserToken:', error);
                }
            })();
        }
        // State 2: With project API token, no user access token
        else if (projectToken) {
            const authConfig = {
                ...config,
                apiKey: projectToken,
                // Ensure no conflicting auth tokens
                accessToken: undefined,
                userKey: undefined
            };
            client = new SubscribeDevClient(authConfig);
            canFetchUsage = false; // Cannot fetch usage without userKey
            canUseStorage = false; // Cannot use remote storage without access token
            console.log('[SubscribeDevStore] Created client with project token auth');
        }
        // State 1: No project API token, no user access token - demo mode
        else {
            const authConfig = {
                ...config,
                // Ensure no auth tokens for demo mode
                accessToken: undefined,
                apiKey: undefined,
                userKey: undefined
            };
            client = new SubscribeDevClient(authConfig);
            canFetchUsage = false; // Cannot fetch usage in demo mode
            canUseStorage = false; // Cannot use remote storage in demo mode
            console.log('[SubscribeDevStore] Created client in demo mode');
        }

        // Set up periodic token checking for URL changes and expiration
        if (typeof window !== 'undefined') {
            const checkForAccessToken = () => {
                const urlToken = extractAndRemoveAccessToken();
                if (urlToken && urlToken !== token) {
                    console.log('[SubscribeDevStore] New access token detected in URL');
                    localStorage.setItem('subscribe-dev-access-token', urlToken);
                    get()._setPersistedToken(urlToken);
                    // Reset sign out state when new token is found
                    get()._setHasSignedOut(false);
                    // Reinitialize with the new token
                    get().initializeAuth({ projectToken, baseUrl, authorizationUrl, accessToken });
                }
            };

            const checkTokenExpiration = () => {
                const state = get();

                // Only check if user is signed in and we have a token
                if (state.isSignedIn && state.token) {
                    if (isTokenExpired(state.token)) {
                        console.log('[SubscribeDevStore] Token expired during session, signing out automatically');
                        get().signOut();
                    }
                }
            };

            // Check immediately
            checkForAccessToken();
            checkTokenExpiration();

            // Listen for navigation events
            const handlePopState = () => checkForAccessToken();
            window.addEventListener('popstate', handlePopState);

            // Check for URL changes periodically
            setInterval(checkForAccessToken, 1000);

            // Check for token expiration every 10 seconds
            setInterval(checkTokenExpiration, 10000);

            // Cleanup function (note: this won't be called in Zustand, but documents the intended cleanup)
            // return () => {
            //     window.removeEventListener('popstate', handlePopState);
            //     clearInterval(urlCheckInterval);
            //     clearInterval(expirationCheckInterval);
            // };
        }

        set({
            isReady: true,
            isSignedIn,
            token,
            user,
            client,
            canFetchUsage,
            canUseStorage
        });
        
        console.log('[SubscribeDevStore] Auth initialized:', {
            isReady: true,
            isSignedIn,
            canFetchUsage,
            canUseStorage
        });
    },

    signIn: () => {
        const state = get();

        // Check if we have a deferred access token
        if (state.deferredAccessToken) {
            console.log('[SubscribeDevStore] Using deferred access token');
            // Sign in with the deferred token
            const token = state.deferredAccessToken;
            const user = decodeJWT(token);

            // Store in localStorage for persistence
            if (typeof window !== 'undefined') {
                localStorage.setItem('subscribe-dev-access-token', token);
            }

            // Create client with the deferred access token
            const { originalInitParams } = state;
            const config: SubscribeDevClientConfig = {
                debug: true
            };

            if (originalInitParams?.baseUrl) {
                config.baseUrl = originalInitParams.baseUrl;
            }
            if (originalInitParams?.authorizationUrl) {
                config.authorizationUrl = originalInitParams.authorizationUrl;
            }

            const authConfig = {
                ...config,
                accessToken: token,
                userKey: token,
                apiKey: undefined
            };
            const client = new SubscribeDevClient(authConfig);

            set({
                token,
                isSignedIn: true,
                user,
                client,
                deferredAccessToken: undefined, // Clear it after use
                canFetchUsage: true,
                canUseStorage: true
            });

            console.log('[SubscribeDevStore] Signed in with deferred token');
            return;
        }

        if (!state.client) return;

        const redirectUrl = new URL(state.client.authorizationUrl);

        // Add project token as parameter if available
        // Note: We need to access projectToken from somewhere, for now we'll use a basic redirect
        // const projectToken = /* get from somewhere */;
        // if (projectToken) {
        //     redirectUrl.searchParams.set('project_token', projectToken);
        // }

        // Add return URL
        redirectUrl.searchParams.set('redirect_uri', window.location.href);

        // Redirect to authorization URL
        window.location.href = redirectUrl.toString();
    },

    signOut: () => {
        if (typeof window !== 'undefined') {
            localStorage.removeItem('subscribe-dev-access-token');
        }
        
        const state = get();
        
        // Set hasSignedOut first
        set(prevState => ({
            ...prevState,
            hasSignedOut: true,
            isSignedIn: false,
            token: null,
            user: null,
            canFetchUsage: false,
            canUseStorage: false
        }));
        
        // Reinitialize with original parameters but exclude any user tokens to return to pre-sign-in state
        if (state.originalInitParams) {
            const { projectToken, baseUrl, authorizationUrl } = state.originalInitParams;
            // Don't pass accessToken, userToken, or getUserToken - return to project-only state
            get().initializeAuth({ projectToken, baseUrl, authorizationUrl });
        }
    },

    setSubscriptionModalUrl: (url) => {
        set({ subscriptionModalUrl: url });
    },

    _setPersistedToken: (token) => {
        // This is an internal action for token updates
        set(state => ({
            ...state,
            token,
            isSignedIn: !!token,
            user: token ? decodeJWT(token) : null
        }));
    },

    _setHasSignedOut: (hasSignedOut) => {
        set({ hasSignedOut });
    },

    handleAuthCallback: (accessToken) => {
        console.log('[SubscribeDevStore] Handling auth callback with new access token');

        // Store the new token in localStorage
        if (typeof window !== 'undefined') {
            localStorage.setItem('subscribe-dev-access-token', accessToken);
        }
        
        // Reset sign out state
        set(state => ({
            ...state,
            hasSignedOut: false
        }));
        
        // Trigger proper reinitialization using stored original parameters
        const state = get();
        if (state.originalInitParams) {
            console.log('[SubscribeDevStore] Reinitializing with new access token');
            // Reinitialize with the new access token, preserving other original parameters
            const { projectToken, baseUrl, authorizationUrl, userToken, getUserToken } = state.originalInitParams;
            get().initializeAuth({ 
                projectToken, 
                baseUrl, 
                authorizationUrl, 
                accessToken, // Use the new access token
                userToken, 
                getUserToken 
            });
        } else {
            console.warn('[SubscribeDevStore] No original init params found, cannot reinitialize');
        }
    },

    createClientWithUserToken: (userToken: string) => {
        const state = get();
        const { originalInitParams } = state;
        
        if (!originalInitParams || !originalInitParams.projectToken) {
            console.warn('[SubscribeDevStore] Cannot create client with user token - missing project token');
            return;
        }

        // Create client configuration
        const config: SubscribeDevClientConfig = {
            debug: true
        };

        if (originalInitParams.baseUrl) {
            config.baseUrl = originalInitParams.baseUrl;
        }
        if (originalInitParams.authorizationUrl) {
            config.authorizationUrl = originalInitParams.authorizationUrl;
        }

        // Create client with project token + user token
        const authConfig = {
            ...config,
            apiKey: originalInitParams.projectToken,
            userKey: userToken, // userToken serves as userKey for user-specific operations
            accessToken: undefined
        };

        const client = new SubscribeDevClient(authConfig);
        const user = decodeJWT(userToken);

        console.log('[SubscribeDevStore] Created client with user token from getUserToken');

        set({
            client,
            token: userToken,
            user,
            isSignedIn: true,
            canFetchUsage: true, // Can fetch usage with userKey
            canUseStorage: false // Cannot use remote storage without access token
        });
    }
}));